import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faDownload,
  faPrint,
  faEye,
  faList,
  faRefresh,
  faCalendarAlt,
  faUser,
  faUsers,
  faFileAlt,
  faFilePdf,
  faFileExcel,
  faCheckCircle,
  faTimes,
  faEdit,
  faReceipt,
  faHistory,
  faBell,
  faEnvelope,
  faPhone,
  faHome,
  faIdCard,
  faUniversity,
  faBook,
  faGraduationCap,
  faBuilding,
  faSchool,
  faVenusMars,
  faAddressCard,
  faPercentage,
  faCalendarDay,
  faSort,
  faSortUp,
  faSortDown,
  faChevronDown,
  faChevronUp,
  faEllipsisV,
  faInfoCircle,
  faTable,
  faLayerGroup,
  faChevronRight,
  faChartLine,
  faClipboardList,
  faFileSignature,
  faUserGraduate,
  faCheckSquare,
  faSquare,
  faArrowRight,
  faCog,
  faExternalLinkAlt
} from "@fortawesome/free-solid-svg-icons";
import "./dashboard.css";
import "./RMS.css";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const ResultManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State for data
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_students: 0,
    total_exam_records: 0,
    exams_by_type: {},
    classes_count: 0,
    sections_count: 0
  });

  // State for filters
  const [filters, setFilters] = useState({
    classFilter: "",
    sectionFilter: "",
    searchQuery: "",
    selectedClasses: [],
    selectedSections: []
  });

  // State for UI
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  
  // CHANGED: Default to false (hidden by default) and added showFilters state
  const [showFilters, setShowFilters] = useState(false);
  
  // NEW: State for classes and sections from exam_marks
  const [classesFromExams, setClassesFromExams] = useState([]);
  const [sectionsForSelectedClass, setSectionsForSelectedClass] = useState([]);
  
  // State for modals
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [showExamTypeModal, setShowExamTypeModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedForResult, setSelectedForResult] = useState([]);
  const [isBulkGenerate, setIsBulkGenerate] = useState(false);
  
  // Refs
  const searchRef = useRef(null);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchStudents();
    fetchExamTypes();
    fetchStats();
    fetchClassesFromExams(); // NEW: Fetch classes from exam_marks
  }, []);

  // Fetch sections when class filter changes
  useEffect(() => {
    if (filters.classFilter) {
      fetchSectionsForClass(filters.classFilter);
    } else {
      setSectionsForSelectedClass([]); // Clear sections when no class selected
    }
  }, [filters.classFilter]);

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, students, sortConfig]);

  // Handle search suggestions
  useEffect(() => {
    if (filters.searchQuery && filters.searchQuery.length > 0) {
      const suggestions = generateSearchSuggestions(filters.searchQuery);
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [filters.searchQuery, students]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // NEW: Fetch classes from exam_marks table
  const fetchClassesFromExams = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ResultController.php?action=getExamClasses`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setClassesFromExams(result.classes);
        }
      }
    } catch (error) {
      console.error("Error fetching classes from exams:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load classes from exam records.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  // NEW: Fetch sections for selected class from exam_marks
  const fetchSectionsForClass = async (className) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ResultController.php?action=getSectionsForClass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ class: className })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSectionsForSelectedClass(result.sections);
          
          // Clear section filter if the current section is not available for the new class
          if (filters.sectionFilter && !result.sections.includes(filters.sectionFilter)) {
            setFilters(prev => ({ ...prev, sectionFilter: "" }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching sections for class:", error);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      // CHANGED: Fetch students from exam_marks table (only those who have exam records)
      const response = await fetch(`${API_BASE_URL}/ResultController.php?action=getExamBasedStudents`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setStudents(result.data);
        setFilteredStudents(result.data);
      } else {
        setStudents([]);
        setFilteredStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load students with exam records.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExamTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ResultController.php?action=getExamTypes`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setExamTypes(result.exam_types);
        }
      }
    } catch (error) {
      console.error("Error fetching exam types:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ResultController.php?action=getStats`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStats(result.stats);
        }
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const generateSearchSuggestions = (query) => {
    const queryLower = query.toLowerCase();
    const suggestions = [];
    
    students.forEach(student => {
      const matches = [];
      
      // Check name
      if (student.name.toLowerCase().includes(queryLower)) {
        matches.push(`Name: ${student.name}`);
      }
      
      // Check student ID
      if (student.student_id.toLowerCase().includes(queryLower)) {
        matches.push(`ID: ${student.student_id}`);
      }
      
      // Check father name
      if (student.father_name && student.father_name.toLowerCase().includes(queryLower)) {
        matches.push(`Father: ${student.father_name}`);
      }
      
      // Check class
      if (student.class.toLowerCase().includes(queryLower)) {
        matches.push(`Class: ${student.class}`);
      }
      
      // Check section
      if (student.section && student.section.toLowerCase().includes(queryLower)) {
        matches.push(`Section: ${student.section}`);
      }
      
      if (matches.length > 0) {
        suggestions.push({
          student,
          matches,
          display: `${student.name} (${student.student_id}) - ${student.class}${student.section ? `-${student.section}` : ''}`
        });
      }
    });
    
    return suggestions.slice(0, 10); // Limit to 10 suggestions
  };

  const applyFilters = () => {
    let filtered = [...students];

    // Apply class filter
    if (filters.classFilter) {
      filtered = filtered.filter(student => 
        student.class && student.class.toLowerCase().includes(filters.classFilter.toLowerCase())
      );
    }

    // Apply section filter
    if (filters.sectionFilter) {
      filtered = filtered.filter(student => 
        student.section && student.section.toLowerCase().includes(filters.sectionFilter.toLowerCase())
      );
    }

    // Apply search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(student =>
        (student.name && student.name.toLowerCase().includes(query)) ||
        (student.father_name && student.father_name.toLowerCase().includes(query)) ||
        (student.student_id && student.student_id.toLowerCase().includes(query)) ||
        (student.class && student.class.toLowerCase().includes(query)) ||
        (student.section && student.section.toLowerCase().includes(query)) ||
        (student.b_form_no && student.b_form_no.toLowerCase().includes(query)) ||
        (student.mobile && student.mobile.includes(query))
      );
    }

    // Apply class selection (if any)
    if (filters.selectedClasses.length > 0) {
      filtered = filtered.filter(student => 
        filters.selectedClasses.includes(student.class)
      );
    }

    // Apply section selection (if any)
    if (filters.selectedSections.length > 0) {
      filtered = filtered.filter(student => 
        filters.selectedSections.includes(student.section)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'class') {
        // Special sorting for class (numeric + alphanumeric)
        aValue = extractClassNumber(a.class);
        bValue = extractClassNumber(b.class);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredStudents(filtered);
    setCurrentPage(1);
  };

  const extractClassNumber = (className) => {
    if (!className) return 0;
    const match = className.match(/\d+/);
    return match ? parseInt(match[0]) : className;
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // FIXED: Select ALL students in the FILTERED list (not just current page)
  const selectAllStudents = () => {
    const allFilteredStudentIds = filteredStudents.map(student => student.id);
    
    // Check if all filtered students are already selected
    const allSelected = allFilteredStudentIds.every(id => selectedStudents.includes(id));
    
    if (allSelected) {
      // If all are selected, deselect ALL filtered students
      setSelectedStudents([]);
    } else {
      // If not all are selected, select ALL filtered students
      setSelectedStudents(allFilteredStudentIds);
    }
  };

  // Remove the selectAllFilteredStudents function since it's now the same as selectAllStudents
  // We'll keep only one "Select All" functionality

  const openStudentDetailsModal = (student) => {
    // Fetch complete student details
    setLoading(true);
    fetch(`${API_BASE_URL}/ResultController.php?action=getStudentDetails&student_id=${student.student_id}`)
      .then(response => response.json())
      .then(result => {
        if (result.success) {
          setSelectedStudent(result.student);
          setShowStudentDetails(true);
        } else {
          Swal.fire({
            title: "Error!",
            text: "Failed to load student details.",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      })
      .catch(error => {
        console.error("Error fetching student details:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to load student details.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openExamTypeModal = (student = null) => {
    if (student) {
      // Single student
      setSelectedForResult([student]);
      setIsBulkGenerate(false);
    } else {
      // Bulk generate for selected students
      const selectedStudentsData = students.filter(s => selectedStudents.includes(s.id));
      if (selectedStudentsData.length === 0) {
        Swal.fire({
          title: "No Students Selected",
          text: "Please select at least one student to generate result card.",
          icon: "warning",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }
      setSelectedForResult(selectedStudentsData);
      setIsBulkGenerate(true);
    }
    setShowExamTypeModal(true);
  };

  const handleExamTypeSelect = (examType) => {
    setShowExamTypeModal(false);
    
    // Navigate to appropriate screen based on exam type
    let route = "";
    switch(examType) {
      case "1st Assessment":
        route = "/Second Assessment";
        break;
      case "2nd Assessment":
        route = "/Second Assessment";
        break;
      case "Midterm":
        route = "/Second Assessment";
        break;
      case "Final":
        route = "/Second Assessment";
        break;
      default:
        route = "/Second Assessment";
    }
    
    // Pass selected students and exam type as state
    navigate(route, { 
      state: { 
        students: selectedForResult,
        examType: examType,
        isBulk: isBulkGenerate
      }
    });
  };

  const clearFilters = () => {
    setFilters({
      classFilter: "",
      sectionFilter: "",
      searchQuery: "",
      selectedClasses: [],
      selectedSections: []
    });
    setSelectedStudents([]);
    setSectionsForSelectedClass([]); // Clear sections
  };

  const refreshData = () => {
    fetchStudents();
    fetchExamTypes();
    fetchStats();
    fetchClassesFromExams(); // Refresh classes from exams
    Swal.fire({
      title: "Refreshed!",
      text: "Data has been refreshed",
      icon: "success",
      timer: 1500,
      showConfirmButton: false
    });
  };

  const exportData = (format) => {
    const dataToExport = selectedStudents.length > 0 
      ? students.filter(s => selectedStudents.includes(s.id))
      : filteredStudents;
    
    if (format === 'csv') {
      exportToCSV(dataToExport);
    } else if (format === 'pdf') {
      exportToPDF(dataToExport);
    }
  };

  const exportToCSV = (data) => {
    const headers = ['ID', 'Student ID', 'Name', 'Father Name', 'Class', 'Section', 'Gender', 'Mobile', 'B-Form No'];
    const csvContent = [
      headers.join(','),
      ...data.map(student => [
        student.id,
        `"${student.student_id}"`,
        `"${student.name}"`,
        `"${student.father_name || ''}"`,
        `"${student.class || ''}"`,
        `"${student.section || ''}"`,
        `"${student.gender || ''}"`,
        `"${student.mobile || ''}"`,
        `"${student.b_form_no || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `students_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    Swal.fire({
      title: "Export Successful!",
      text: "Data exported as CSV successfully",
      icon: "success",
      confirmButtonColor: "#10b981",
    });
  };

  const exportToPDF = (data) => {
    // Similar to FeeDefaulters PDF export logic
    Swal.fire({
      title: "PDF Export",
      text: "This feature will be implemented soon",
      icon: "info",
      confirmButtonColor: "#3b82f6",
    });
  };

  // Get unique classes from exam_marks
  const uniqueClasses = classesFromExams.sort((a, b) => {
    const extractNum = (cls) => {
      if (!cls) return 0;
      const match = cls.match(/\d+/);
      return match ? parseInt(match[0]) : cls;
    };
    return extractNum(a) - extractNum(b);
  });

  // Get unique sections based on selected class
  const uniqueSections = sectionsForSelectedClass.sort();

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredStudents.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const halfMax = Math.floor(maxPagesToShow / 2);
      let start = Math.max(currentPage - halfMax, 1);
      let end = Math.min(start + maxPagesToShow - 1, totalPages);
      
      if (end - start + 1 < maxPagesToShow) {
        start = Math.max(end - maxPagesToShow + 1, 1);
      }
      
      if (start > 1) pageNumbers.push(1);
      if (start > 2) pageNumbers.push('...');
      
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
      
      if (end < totalPages - 1) pageNumbers.push('...');
      if (end < totalPages) pageNumbers.push(totalPages);
    }

    return (
      <div className="ResultManagement-pagination">
        <button 
          className="ResultManagement-pageBtn"
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <FontAwesomeIcon icon={faChevronDown} rotation={90} />
        </button>
        
        {pageNumbers.map((number, index) => (
          number === '...' ? (
            <span key={`ellipsis-${index}`} className="ResultManagement-pageEllipsis">...</span>
          ) : (
            <button
              key={number}
              className={`ResultManagement-pageBtn ${currentPage === number ? 'ResultManagement-pageBtnActive' : ''}`}
              onClick={() => paginate(number)}
            >
              {number}
            </button>
          )
        ))}
        
        <button 
          className="ResultManagement-pageBtn"
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <FontAwesomeIcon icon={faChevronDown} rotation={-90} />
        </button>
      </div>
    );
  };

  const renderStudentDetailsModal = () => {
    if (!selectedStudent || !showStudentDetails) return null;

    return (
      <div className="ResultManagement-modal-overlay">
        <div className="ResultManagement-modal ResultManagement-student-details-modal">
          <div className="ResultManagement-modal-header">
            <h3>
              <FontAwesomeIcon icon={faUserGraduate} />
              Student Details - {selectedStudent.name}
            </h3>
            <button 
              className="ResultManagement-modal-close"
              onClick={() => setShowStudentDetails(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="ResultManagement-modal-content">
            <div className="ResultManagement-student-details-grid">
              <div className="ResultManagement-detail-section">
                <h4>Personal Information</h4>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Student ID:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.student_id}</span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Full Name:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.name}</span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Father's Name:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.father_name || 'N/A'}</span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Gender:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.gender || 'N/A'}</span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">B-Form No:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.b_form_no || 'N/A'}</span>
                </div>
              </div>
              
              <div className="ResultManagement-detail-section">
                <h4>Contact Information</h4>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Mobile:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.mobile || 'N/A'}</span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Address:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.address || 'N/A'}</span>
                </div>
              </div>
              
              <div className="ResultManagement-detail-section">
                <h4>Academic Information</h4>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Class:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.class || 'N/A'}</span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Section:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.section || 'N/A'}</span>
                </div>
              </div>
              
              <div className="ResultManagement-detail-section">
                <h4>Family Information</h4>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Family Status:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.family_status || 'Solo'}</span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Family Code:</span>
                  <span className="ResultManagement-detail-value">{selectedStudent.family_code || 'N/A'}</span>
                </div>
              </div>
              
              <div className="ResultManagement-detail-section">
                <h4>System Information</h4>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Created At:</span>
                  <span className="ResultManagement-detail-value">
                    {selectedStudent.created_at ? new Date(selectedStudent.created_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="ResultManagement-detail-row">
                  <span className="ResultManagement-detail-label">Last Updated:</span>
                  <span className="ResultManagement-detail-value">
                    {selectedStudent.updated_at ? new Date(selectedStudent.updated_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="ResultManagement-modal-footer">
            <button 
              className="ResultManagement-btn ResultManagement-btnSecondary"
              onClick={() => setShowStudentDetails(false)}
            >
              Close
            </button>
            <button 
              className="ResultManagement-btn ResultManagement-btnPrimary"
              onClick={() => {
                setShowStudentDetails(false);
                openExamTypeModal(selectedStudent);
              }}
            >
              <FontAwesomeIcon icon={faFileAlt} /> Generate Result Card
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderExamTypeModal = () => {
    if (!showExamTypeModal) return null;

    return (
      <div className="ResultManagement-modal-overlay">
        <div className="ResultManagement-modal ResultManagement-exam-type-modal">
          <div className="ResultManagement-modal-header">
            <h3>
              <FontAwesomeIcon icon={faClipboardList} />
              Select Exam Type
            </h3>
            <button 
              className="ResultManagement-modal-close"
              onClick={() => setShowExamTypeModal(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="ResultManagement-modal-content">
            <div className="ResultManagement-exam-type-info">
              <p>
                <FontAwesomeIcon icon={faUserGraduate} />
                {isBulkGenerate 
                  ? `Generate result cards for ${selectedForResult.length} selected students`
                  : `Generate result card for ${selectedForResult[0]?.name} (${selectedForResult[0]?.student_id})`
                }
              </p>
            </div>
            
            <div className="ResultManagement-exam-types-grid">
              {examTypes.map((examType, index) => (
                <div 
                  key={index}
                  className="ResultManagement-exam-type-card"
                  onClick={() => handleExamTypeSelect(examType)}
                >
                  <div className="ResultManagement-exam-type-icon">
                    {index === 0 && <FontAwesomeIcon icon={faFileSignature} />}
                    {index === 1 && <FontAwesomeIcon icon={faFileSignature} />}
                    {index === 2 && <FontAwesomeIcon icon={faChartLine} />}
                    {index === 3 && <FontAwesomeIcon icon={faGraduationCap} />}
                  </div>
                  <div className="ResultManagement-exam-type-content">
                    <h4>{examType}</h4>
                    <p>Generate {examType.toLowerCase()} result card</p>
                  </div>
                  <div className="ResultManagement-exam-type-arrow">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="ResultManagement-modal-footer">
            <button 
              className="ResultManagement-btn ResultManagement-btnSecondary"
              onClick={() => setShowExamTypeModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && students.length === 0) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="main-content" style={{ marginLeft: "20%" }}>
          <div className="ResultManagement-loading">
            <div className="ResultManagement-spinner"></div>
            <p>Loading result management data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <div className="main-content" style={{ marginLeft: "20%" }}>
        <section className="content">
          {/* Header Section */}
          <div className="ResultManagement-header">
            <div className="ResultManagement-headerContent">
              <h1 className="ResultManagement-title">
                <FontAwesomeIcon icon={faClipboardList} className="ResultManagement-headerIcon" />
                Result Management Dashboard
              </h1>
              <p className="ResultManagement-subtitle">
                Manage student results, generate report cards, and track academic performance
              </p>
            </div>
            
            <div className="ResultManagement-headerActions">
              {/* REPLACED: Standard button with new dark toggle switch */}
              <div className="ResultManagement-toggle-container" onClick={() => setShowFilters(!showFilters)}>
                <span className="ResultManagement-toggle-text">
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </span>
                <div className={`ResultManagement-toggle-switch ${showFilters ? "active" : ""}`}>
                  <div className="ResultManagement-toggle-slider"></div>
                </div>
              </div>
              
              {/* <div className="ResultManagement-dropdown">
                <button className="ResultManagement-btn ResultManagement-btnExport">
                  <FontAwesomeIcon icon={faDownload} /> Export
                </button>
                <div className="ResultManagement-dropdownContent">
                  <a href="#" onClick={(e) => { e.preventDefault(); exportData('csv'); }}>
                    <FontAwesomeIcon icon={faFileExcel} /> Export as CSV
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); exportData('pdf'); }}>
                    <FontAwesomeIcon icon={faFilePdf} /> Export as PDF
                  </a>
                </div>
              </div> */}
              
              {/* REMOVED: The separate "Select All Filtered" button since header checkbox now does this */}
              
              <button 
                className="ResultManagement-btn ResultManagement-btnPrimary"
                onClick={() => openExamTypeModal()}
                disabled={selectedStudents.length === 0}
              >
                <FontAwesomeIcon icon={faFileAlt} />
                Generate Result Cards ({selectedStudents.length})
              </button>
              
              <button 
                className="ResultManagement-btn ResultManagement-btnRefresh"
                onClick={refreshData}
                title="Refresh Data"
              >
                <FontAwesomeIcon icon={faRefresh} />
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="ResultManagement-statsOverview">
            <div className="ResultManagement-statCard">
              <div className="ResultManagement-statIcon ResultManagement-statTotal">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="ResultManagement-statDetails">
                <div className="ResultManagement-statValue">{stats.total_students}</div>
                <div className="ResultManagement-statLabel">Total Students</div>
                <div className="ResultManagement-statSubtext">
                  {uniqueClasses.length} Classes • {uniqueSections.length} Sections
                </div>
              </div>
            </div>
            
            <div className="ResultManagement-statCard">
              <div className="ResultManagement-statIcon ResultManagement-statExams">
                <FontAwesomeIcon icon={faFileAlt} />
              </div>
              <div className="ResultManagement-statDetails">
                <div className="ResultManagement-statValue">{stats.total_exam_records}</div>
                <div className="ResultManagement-statLabel">Exam Records</div>
                <div className="ResultManagement-statSubtext">
                  {Object.keys(stats.exams_by_type || {}).length} Exam Types
                </div>
              </div>
            </div>
            
            <div className="ResultManagement-statCard">
              <div className="ResultManagement-statIcon ResultManagement-statSelected">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="ResultManagement-statDetails">
                <div className="ResultManagement-statValue">{selectedStudents.length}</div>
                <div className="ResultManagement-statLabel">Selected Students</div>
                <div className="ResultManagement-statSubtext">
                  {filteredStudents.length > 0 
                    ? `${((selectedStudents.length / filteredStudents.length) * 100).toFixed(1)}% of filtered`
                    : '0% of filtered'
                  }
                </div>
              </div>
            </div>
            
            <div className="ResultManagement-statCard">
              <div className="ResultManagement-statIcon ResultManagement-statFiltered">
                <FontAwesomeIcon icon={faFilter} />
              </div>
              <div className="ResultManagement-statDetails">
                <div className="ResultManagement-statValue">{filteredStudents.length}</div>
                <div className="ResultManagement-statLabel">Filtered Students</div>
                <div className="ResultManagement-statSubtext">
                  {students.length > 0 
                    ? `${((filteredStudents.length / students.length) * 100).toFixed(1)}% of total`
                    : '0% of total'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className="ResultManagement-filterSection">
              <div className="ResultManagement-filterHeader">
                <h3>
                  <FontAwesomeIcon icon={faFilter} /> Filters
                </h3>
                <div className="ResultManagement-filterActions">
                  <button 
                    className="ResultManagement-clearFilters"
                    onClick={clearFilters}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Clear All
                  </button>
                  <button 
                    className="ResultManagement-btn ResultManagement-btnApply"
                    onClick={applyFilters}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
              
              <div className="ResultManagement-filterGrid">
                <div className="ResultManagement-filterRow">
                  <div className="ResultManagement-filterGroup">
                    <label className="ResultManagement-filterLabel">
                      <FontAwesomeIcon icon={faSchool} /> Class
                    </label>
                    <div className="ResultManagement-classSelect">
                      <FontAwesomeIcon icon={faGraduationCap} className="ResultManagement-classIcon" />
                      <select
                        value={filters.classFilter}
                        onChange={(e) => setFilters(prev => ({ 
                          ...prev, 
                          classFilter: e.target.value,
                          sectionFilter: "" // Clear section when class changes
                        }))}
                        className="ResultManagement-classSelectInput"
                      >
                        <option value="">All Classes</option>
                        {uniqueClasses.map((cls, index) => (
                          <option key={index} value={cls}>{cls}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="ResultManagement-filterGroup">
                    <label className="ResultManagement-filterLabel">
                      <FontAwesomeIcon icon={faLayerGroup} /> Section
                    </label>
                    <div className="ResultManagement-sectionSelect">
                      <FontAwesomeIcon icon={faUsers} className="ResultManagement-sectionIcon" />
                      <select
                        value={filters.sectionFilter}
                        onChange={(e) => setFilters(prev => ({ ...prev, sectionFilter: e.target.value }))}
                        className="ResultManagement-sectionSelectInput"
                        disabled={!filters.classFilter} // Disable if no class selected
                        title={!filters.classFilter ? "Please select a class first" : ""}
                      >
                        <option value="">All Sections</option>
                        {uniqueSections.map((section, index) => (
                          <option key={index} value={section}>{section}</option>
                        ))}
                      </select>
                      {!filters.classFilter && (
                        <div className="ResultManagement-filterNote">
                          Select a class first
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Search with suggestions */}
                <div className="ResultManagement-filterGroup ResultManagement-searchGroup" ref={searchRef}>
                  <label className="ResultManagement-filterLabel">
                    <FontAwesomeIcon icon={faSearch} /> Search Students
                  </label>
                  <div className="ResultManagement-searchBox">
                    <FontAwesomeIcon icon={faSearch} className="ResultManagement-searchIcon" />
                    <input
                      type="text"
                      placeholder="Search by name, ID, father name, class, section..."
                      value={filters.searchQuery}
                      onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                      className="ResultManagement-searchInput"
                      onFocus={() => setShowSuggestions(true)}
                    />
                    {filters.searchQuery && (
                      <button 
                        className="ResultManagement-clearSearch"
                        onClick={() => setFilters(prev => ({ ...prev, searchQuery: "" }))}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                  
                  {/* Search Suggestions */}
                  {showSuggestions && searchSuggestions.length > 0 && (
                    <div className="ResultManagement-searchSuggestions">
                      {searchSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className="ResultManagement-suggestionItem"
                          onClick={() => {
                            setFilters(prev => ({ ...prev, searchQuery: suggestion.student.name }));
                            setShowSuggestions(false);
                          }}
                        >
                          <div className="ResultManagement-suggestionText">
                            {suggestion.display}
                          </div>
                          <div className="ResultManagement-suggestionMatches">
                            {suggestion.matches.slice(0, 2).map((match, idx) => (
                              <span key={idx} className="ResultManagement-suggestionMatch">
                                {match}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="ResultManagement-searchTips">
                    <small>Search tips: Type student name, ID, father name, class, or section</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="ResultManagement-resultsSummary">
            <div className="ResultManagement-resultsInfo">
              <div className="ResultManagement-resultsStats">
                <span className="ResultManagement-resultsCount">
                  <strong>{filteredStudents.length}</strong> students found
                </span>
                <span className="ResultManagement-resultsSelected">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  {selectedStudents.length} selected ({selectedStudents.length === filteredStudents.length && filteredStudents.length > 0 ? 'All' : `${Math.round((selectedStudents.length / filteredStudents.length) * 100)}%`} of filtered)
                </span>
              </div>
              <div className="ResultManagement-viewControls">
                <button 
                  className={`ResultManagement-viewBtn ${viewMode === 'table' ? 'ResultManagement-viewBtnActive' : ''}`}
                  onClick={() => setViewMode('table')}
                  title="Table View"
                >
                  <FontAwesomeIcon icon={faTable} />
                </button>
              </div>
            </div>
            <div className="ResultManagement-pageControls">
              <div className="ResultManagement-pageSize">
                <label>Show:</label>
                <select
                  value={recordsPerPage}
                  onChange={(e) => {
                    setRecordsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="ResultManagement-pageSelect"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>per page</span>
              </div>
            </div>
          </div>

          {/* Table View */}
          <div className="ResultManagement-tableContainer">
            <div className="ResultManagement-tableWrapper">
              <table className="ResultManagement-table">
                <thead>
                  <tr>
                    <th className="ResultManagement-tableCheckbox">
                      <input
                        type="checkbox"
                        // Check if ALL filtered students are selected
                        checked={filteredStudents.length > 0 && filteredStudents.every(student => selectedStudents.includes(student.id))}
                        onChange={selectAllStudents}
                        title="Select/Deselect all students in filtered list"
                      />
                    </th>
                    <th 
                      className="ResultManagement-tableHeader"
                      onClick={() => handleSort("student_id")}
                    >
                      Student ID
                      {sortConfig.key === "student_id" && (
                        <FontAwesomeIcon 
                          icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} 
                          className="ResultManagement-sortIcon"
                        />
                      )}
                    </th>
                    <th 
                      className="ResultManagement-tableHeader"
                      onClick={() => handleSort("name")}
                    >
                      Student Details
                      {sortConfig.key === "name" && (
                        <FontAwesomeIcon 
                          icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} 
                          className="ResultManagement-sortIcon"
                        />
                      )}
                    </th>
                    <th 
                      className="ResultManagement-tableHeader"
                      onClick={() => handleSort("class")}
                    >
                      Class & Section
                      {sortConfig.key === "class" && (
                        <FontAwesomeIcon 
                          icon={sortConfig.direction === "asc" ? faSortUp : faSortDown} 
                          className="ResultManagement-sortIcon"
                        />
                      )}
                    </th>
                    <th className="ResultManagement-tableHeader">Contact Info</th>
                    <th className="ResultManagement-tableHeader">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentStudents.map((student, index) => (
                    <tr key={student.id} className={selectedStudents.includes(student.id) ? "ResultManagement-rowSelected" : ""}>
                      <td className="ResultManagement-tableCheckbox">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleSelectStudent(student.id)}
                        />
                      </td>
                      <td className="ResultManagement-studentId">
                        <div className="ResultManagement-idCell">
                          <FontAwesomeIcon icon={faIdCard} />
                          <strong>{student.student_id}</strong>
                        </div>
                      </td>
                      <td className="ResultManagement-studentCell">
                        <div className="ResultManagement-studentInfo">
                          <strong className="ResultManagement-studentName">{student.name}</strong>
                          <div className="ResultManagement-studentDetails">
                            {student.father_name && (
                              <span className="ResultManagement-fatherName">
                                <FontAwesomeIcon icon={faUser} /> {student.father_name}
                              </span>
                            )}
                            <span className="ResultManagement-gender">
                              <FontAwesomeIcon icon={faVenusMars} /> {student.gender || 'N/A'}
                            </span>
                            {student.b_form_no && (
                              <span className="ResultManagement-bForm">
                                <FontAwesomeIcon icon={faAddressCard} /> B-Form: {student.b_form_no}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="ResultManagement-classInfo">
                          <div className="ResultManagement-classSection">
                            <FontAwesomeIcon icon={faGraduationCap} />
                            <span className="ResultManagement-className">{student.class || "N/A"}</span>
                            {student.section && (
                              <span className="ResultManagement-section">Sec: {student.section}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="ResultManagement-contactInfo">
                          {student.mobile && (
                            <div className="ResultManagement-mobile">
                              <FontAwesomeIcon icon={faPhone} /> {student.mobile}
                            </div>
                          )}
                          {student.address && (
                            <div className="ResultManagement-address">
                              <FontAwesomeIcon icon={faHome} /> 
                              <span title={student.address}>
                                {student.address.length > 30 ? `${student.address.substring(0, 30)}...` : student.address}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="ResultManagement-actionButtons">
                          <button
                            onClick={() => openStudentDetailsModal(student)}
                            title="View Student Details"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "6px 12px",
                              fontSize: "13px",
                              fontWeight: "500",
                              color: "#1e40af",
                              backgroundColor: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              borderRadius: "6px",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              transition: "all 0.2s ease-in-out"
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} style={{ fontSize: "13px" }} />
                            View
                          </button>

                          <button 
                            className="ResultManagement-actionBtn ResultManagement-resultBtn"
                            onClick={() => openExamTypeModal(student)}
                            title="Generate Result Card"
                          >
                            <FontAwesomeIcon icon={faFileAlt} /> Result Card
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {currentStudents.length === 0 && (
              <div className="ResultManagement-noData">
                <FontAwesomeIcon icon={faSearch} size="3x" />
                <h3>No students found</h3>
                <p>Try adjusting your filters or search query</p>
                <button 
                  className="ResultManagement-btn ResultManagement-btnSecondary"
                  onClick={clearFilters}
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredStudents.length > recordsPerPage && (
            <div className="ResultManagement-paginationContainer">
              <div className="ResultManagement-paginationInfo">
                <span>
                  Showing {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, filteredStudents.length)} of {filteredStudents.length} students
                </span>
                <span className="ResultManagement-paginationSummary">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
              {renderPagination()}
            </div>
          )}

          {/* Summary Footer */}
          <div className="ResultManagement-summaryFooter">
            <div className="ResultManagement-summaryStats">
              <div className="ResultManagement-summaryItem">
                <span className="ResultManagement-summaryLabel">Total Students:</span>
                <span className="ResultManagement-summaryValue">{filteredStudents.length}</span>
              </div>
              <div className="ResultManagement-summaryItem">
                <span className="ResultManagement-summaryLabel">Selected Students:</span>
                <span className="ResultManagement-summaryValue ResultManagement-summarySelected">
                  {selectedStudents.length}
                </span>
              </div>
              <div className="ResultManagement-summaryItem">
                <span className="ResultManagement-summaryLabel">Exam Types Available:</span>
                <span className="ResultManagement-summaryValue ResultManagement-summaryExams">
                  {examTypes.length}
                </span>
              </div>
            </div>
            <div className="ResultManagement-summaryActions">
              <button 
                className="ResultManagement-btn ResultManagement-btnSecondary"
                onClick={() => {
                  Swal.fire({
                    title: 'Quick Actions',
                    html: `
                      <div style="text-align: left;">
                        <p><strong>Total Students:</strong> ${students.length}</p>
                        <p><strong>Filtered Students:</strong> ${filteredStudents.length}</p>
                        <p><strong>Selected Students:</strong> ${selectedStudents.length}</p>
                        <p><strong>Exam Types:</strong> ${examTypes.join(', ')}</p>
                        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
                      </div>
                    `,
                    icon: 'info',
                    confirmButtonText: 'OK'
                  })
                }}
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Quick Info
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Modals */}
      {renderStudentDetailsModal()}
      {renderExamTypeModal()}
    </div>
  );
};

export default ResultManagement;